<template>
  <div>
    <v-menu v-model="menuOpen" location="bottom start">
      <template v-slot:activator="{ props }">
        <v-chip
          v-bind="props"
          class="entity-chip"
          label
          size="small"
          variant="flat"
          :append-icon="appendIcon || undefined"
        ><span v-if="prefix" class="entity-affix">{{ prefix }}&nbsp;</span>{{ currentDisplayName }}<span v-if="suffix" class="entity-suffix">&nbsp;{{ suffix }}</span></v-chip>
      </template>
      <v-list density="compact">
        <template v-for="entity in quickEntities" :key="entity.name">
          <!-- Corpus selector (oxjob #481, redesigned #523 round 5): when the host opts in (the OQL
               builder passes a `corpus`), the works entry splits into two rows — "works (core)" (the
               curated default) and "works (all)" (core + expansion). They behave like two separate
               entities: picking one sets the subject to works AND the corpus in a single click. -->
          <template v-if="entity.name === 'works' && showCorpus">
            <v-list-item @click="selectWorksCorpus('core')">
              <v-list-item-title>works (core)</v-list-item-title>
              <template #append>
                <v-icon v-if="entityType === 'works' && corpus !== 'all'">mdi-check</v-icon>
              </template>
            </v-list-item>
            <v-list-item @click="selectWorksCorpus('all')">
              <v-list-item-title>works (all)</v-list-item-title>
              <template #append>
                <v-icon v-if="entityType === 'works' && corpus === 'all'">mdi-check</v-icon>
              </template>
            </v-list-item>
          </template>
          <v-list-item v-else @click="selectEntity(entity.name)">
            <v-list-item-title class="text-capitalize">{{ entity.displayName }}</v-list-item-title>
            <template #append>
              <v-icon v-if="entityType === entity.name">mdi-check</v-icon>
            </template>
          </v-list-item>
        </template>
        <v-divider class="my-1" />
        <v-list-item @click="openBrowser">
          <v-list-item-title>More entity types...</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <entity-type-browser-dialog
      v-model="browserOpen"
      @select="selectEntity"
    />
  </div>
</template>

<script setup>
defineOptions({ name: 'EntitySelectorButton' });

import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getEntityConfig } from '@/entityConfigs';
import EntityTypeBrowserDialog from '@/components/EntityTypeBrowserDialog.vue';

// Optional controlled mode (oxjob #428): pass `v-model` to drive the selection
// locally and receive `update:modelValue` instead of the component navigating the
// SERP route / writing Vuex. When modelValue is null (the SERP default) the
// original route/store-driven behavior is unchanged.
const props = defineProps({
  modelValue: { type: String, default: null },
  // The dropdown caret. Defaults to the menu-down chevron (every existing usage).
  // The OQL builder passes `null` to hide it — clicking a builder block always opens
  // its menu, so the caret is redundant chrome there. (oxjob #467.)
  appendIcon: { type: String, default: 'mdi-menu-down' },
  // Optional trailing keyword rendered INSIDE the chip after the entity name. The
  // OQL builder passes "where" so the leading `works where` reads as ONE lavender
  // block instead of an entity chip + a separate `where` keyword (oxjob #467, Jason
  // 2026-06-16). Default empty = no suffix, so every other usage is unchanged.
  suffix: { type: String, default: '' },
  // Optional leading word rendered INSIDE the chip before the entity name. The OQL
  // builder's TOOLBAR selector passes "search" so the control reads "search works ⌄"
  // (oxjob #507 — the entity selector moved out of the canvas into the toolbar).
  // Default empty = no prefix, so every other usage is unchanged.
  prefix: { type: String, default: '' },
  // Optional corpus selector (oxjob #481). When non-null (the OQL builder opts in by
  // passing the current corpus, `core` | `all`), the menu grows a corpus section: a
  // checkbox to include the expansion corpus (unchecked = core, checked = all). Default
  // null = no corpus section, so every other usage (basic SERP) is unchanged.
  corpus: { type: String, default: null },
});
const emit = defineEmits(['entitySelected', 'update:modelValue', 'update:corpus']);

const showCorpus = computed(() => props.corpus !== null);

const route = useRoute();
const router = useRouter();
const store = useStore();

const menuOpen = ref(false);
const browserOpen = ref(false);
const pendingBrowserOpen = ref(false);

const controlled = computed(() => props.modelValue !== null);

const entityType = computed(() => {
  if (controlled.value) return props.modelValue;
  if (route.name === 'Serp' && route.params.entityType) {
    return route.params.entityType;
  }
  return store.state.entityType;
});

const currentDisplayName = computed(() => {
  const config = getEntityConfig(entityType.value);
  const base = config ? config.displayName : 'works';
  // When the corpus selector is enabled (OQL builder) and the subject is works, the corpus rides
  // in the label so the control reads "search works (core)" / "search works (all)" (#523 round 5).
  if (showCorpus.value && entityType.value === 'works') {
    return `${base} (${props.corpus === 'all' ? 'all' : 'core'})`;
  }
  return base;
});

const quickEntities = [
  { name: 'works', displayName: 'works' },
  { name: 'authors', displayName: 'authors' },
  { name: 'institutions', displayName: 'institutions' },
  { name: 'sources', displayName: 'sources' },
];

function selectEntity(name) {
  // Controlled mode: hand the choice to the parent, navigate/commit nothing.
  if (controlled.value) {
    emit('update:modelValue', name);
    emit('entitySelected');
    return;
  }
  // On a results page, switching entity re-runs the search immediately.
  // Off it (e.g. the landing page), just update the selection and let the
  // user finish entering their query — navigation happens on submit.
  if (route.name === 'Serp') {
    router.push({ name: 'Serp', params: { entityType: name } });
  } else {
    store.commit('setEntityType', name);
  }
  emit('entitySelected');
}

// Pick a works corpus (oxjob #523 round 5): the "works (core)" / "works (all)" rows behave like
// sibling entities — selecting one sets the subject to works AND the corpus in one click.
function selectWorksCorpus(c) {
  if (props.corpus !== c) emit('update:corpus', c);
  selectEntity('works');
}

// Open browser after menu finishes closing
watch(menuOpen, (open) => {
  if (!open && pendingBrowserOpen.value) {
    pendingBrowserOpen.value = false;
    // Wait for Vuetify menu close animation to complete
    setTimeout(() => {
      browserOpen.value = true;
    }, 300);
  }
});

function openBrowser() {
  pendingBrowserOpen.value = true;
  menuOpen.value = false;
}

</script>

<style lang="scss" scoped>
/* Rendered as a property-style chip. Inside the OQL builder the colour comes from
   the --prop-bg/--prop-fg vars (matching the field chips); elsewhere (e.g. the SERP
   search box) those vars are unset, so it falls back to a neutral grey chip. */
.entity-chip {
  cursor: pointer;
  text-transform: none;
  background: var(--prop-bg, rgba(0, 0, 0, 0.07)) !important;
  color: var(--prop-fg, rgba(0, 0, 0, 0.87)) !important;
}
/* The trailing `where` keyword (OQL builder): rendered in the SAME colour as the
   entity name (Jason 2026-06-16 — muting it read as broken grey text in practice).
   It still reads as structural via position + the shared lavender box. */
</style>
